import { HttpClient } from '@angular/common/http'
import { Component, OnInit, ViewChild, ElementRef, AfterViewInit } from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import MarkerClusterer from '@google/markerclustererplus'
import { DataTableDirective } from 'angular-datatables'
import * as moment_ from 'moment'
import { Subject, Subscription } from 'rxjs'
import {
	getLengthTable,
	getLoggedUser,
	setLengthTable
} from 'src/app/_helpers/LocalStorage'
import {
	SearchTruncated,
	searchTruncatedTree
} from 'src/app/_helpers/SearchTruncated'
import {
	insertOrRemoveClassInScrollButton,
	scrollToConteiner
} from 'src/app/_helpers/Utils'
import { PATH } from 'src/app/app.api'
import { UserAdmin } from 'src/app/models/UserAdmin'

import { NotificationService } from 'src/app/services/notification.service'
import { Address } from '../../../../models/Address'
import { CompanyService } from '../../../../services/company.service'
import { RegionsService } from '../../../../services/regions.service'

import { TreeControlPanelDto } from './types/tree-maps'
import { Company } from '../../../../models/Company'

import { Region } from 'src/app/models/Region'

import { TreePopUp } from './types/tree-popup'
import { TDataTableParameters } from './types/data-table-parameters'
import { ElementConfig } from './types/maps'
import { TTreeList } from './types/tree-list'
import { TTreeFeature } from './types/tree-feature'
import { CentralControlService } from 'src/app/services/central-control.service'

@Component({
	selector: 'public-panel',
	templateUrl: './public-panel.component.html'
})
export class PublicPanelComponent implements OnInit, AfterViewInit {
	@ViewChild(DataTableDirective) public dtElement: DataTableDirective
	@ViewChild('tableConteiner')
	public tableConteiner: ElementRef<HTMLDivElement>

	// Various data properties
	public refreshTable: Subject<any> = new Subject()
	public datatableOptions: DataTables.Settings = {}
	public trees: Array<TTreeList>
	public address: Address = new Address()
	public company: Company = new Company()
	public sessionUser: UserAdmin = getLoggedUser()
	public companyId: string = localStorage.getItem('companyId')
	public allRegions: Array<Region> = []
	public isLoading: boolean = true
	public isLoadingPublicMapCacheUpdate = false
	public isLoadingMapCacheUpdate = false
	public isReloadingMap = false
	public dataTableParams: TDataTableParameters
	public filterText: string = ''
	public dateStart: string = ''
	public dateEnd: string = ''
	public markers: Array<{
		id: string,
		marker: google.maps.Marker
	}> = []

	public polygonsDelimitAllRegions: Array<google.maps.Polygon> = []
	public prevMarker: google.maps.Marker = null
	public regionInfoWindow: google.maps.InfoWindow
	public infoPopupWindow: google.maps.InfoWindow
	public myMap: google.maps.Map
	public allTrees: Array<TreeControlPanelDto>
	public searchAddress: string = ''
	public markerCluster: MarkerClusterer
	public isAdmin: boolean = false
	public assignments: Array<string> = []
	public viewAllRegionsInMap: boolean = true
	public treeSearchTruncated: Array<SearchTruncated> = searchTruncatedTree
	public searchTruncatedStringFormated: string = ''
	public showScrollUpButton: boolean = false
	public lengthTable: string = getLengthTable()
	public httpListSubscription: Subscription

	constructor(
		private elementRef: ElementRef,
		private readonly centralControlService: CentralControlService,
		private readonly companyService: CompanyService,
		private readonly activateRoute: ActivatedRoute,
		private readonly regionsService: RegionsService,
		private readonly notificationService: NotificationService
	) { }

	/**
	 * Initialize component data on load.
	 */
	public async ngOnInit(): Promise<void> {
		await this.fetchCompany()
		this.setupDatatables()
		await this.getTreesForMap()
		await this.fetchRegions()
		this.filterText = this.activateRoute.snapshot.queryParams.search || ''
		this.isAdmin = this.sessionUser.roles.includes('GENERAL_ADMIN')
		this.sessionUser.assignments.forEach(assignment =>
			this.assignments.push(assignment)
		)

		setTimeout(() => {
			this.getLocation()
		}, 500)

		this.rerender()

		setTimeout(() => {
			this.initMap()
		}, 2000)

		const observer = new IntersectionObserver(([entry]) => {
			this.showScrollUpButton = entry.isIntersecting
			if (entry.isIntersecting) {
				insertOrRemoveClassInScrollButton()
			}
		})
		observer.observe(this.tableConteiner.nativeElement)
	}


	public ngAfterViewInit(): void {
		this.elementRef.nativeElement
			.querySelector('#searchAddress')
			.addEventListener('change', this.searchByCepOrAddress.bind(this))
	}

	/**
	 * Fetch company details.
	 */
	public async fetchCompany(): Promise<void> {
		try {
			this.company = await this.companyService
				.detailCompany(this.companyId)
				.toPromise()
			this.address.street = this.company.address.street
		} catch (err) {
			console.error('Failed to fetch company details', err)
		}
	}

	/**
	 * Get geolocation based on company address.
	 */
	public async getLocation() {
		try {
			if (!google || !google.maps) {
				console.log('Google Maps API not loaded')
				return
			}

			const geocoder = new google.maps.Geocoder()
			geocoder.geocode({ address: this.address.street }, (results: Array<{
				geometry: {
					location: {
						lng: () => number;
						lat: () => number;
					};
				};
			}>,                                                 status: google.maps.GeocoderStatus) => {
				if (status === google.maps.GeocoderStatus.OK) {
					this.address.lat = results[0].geometry.location.lat()
					this.address.lng = results[0].geometry.location.lng()
				}
			})
		} catch (error) {
			console.error('Failed to get geolocation', error)
		}
	}

	/**
	 * Fetch all trees data for the map.
	 */
	public async getTreesForMap(): Promise<void> {
		this.allTrees = await this.centralControlService
			.getMapPins(this.companyId)
			.toPromise()
	}

	/**
	 * Setup DataTables options and configuration.
	 */
	public setupDatatables(): void {
		this.datatableOptions = {
			language: {
				processing: 'Processando...',
				lengthMenu: '_MENU_ resultados por página',
				info: 'Mostrando de _START_ até _END_ de _TOTAL_ registros',
				infoEmpty: 'Mostrando 0 até 0 de 0 registros',
				infoFiltered: '',
				infoPostFix: '',
				zeroRecords: '...',
				loadingRecords: 'Carregando...',
				emptyTable: 'Nenhum registro encontrado',
				paginate: {
					first: 'Primeiro',
					previous: 'Anterior',
					next: 'Próximo',
					last: 'Último'
				},
				aria: {
					sortAscending: ': Ordenar colunas de forma ascendente',
					sortDescending: ': Ordenar colunas de forma descendente'
				}
			},
			pagingType: 'full_numbers',
			searching: true,
			ordering: true,
			info: true,
			autoWidth: false,
			pageLength: Number(this.lengthTable),
			lengthChange: false,
			columnDefs: [],
			order: [[1, 'desc']],
			orderMulti: true,
			serverSide: true,
			processing: true,

			ajax: (
				dataTablesParameters: TDataTableParameters,
				callback: ({
					recordsFiltered
				}: {
					recordsFiltered: number;
					data: Array<any>;
				}) => void
			) => {
				if (this.httpListSubscription) {
					this.httpListSubscription.unsubscribe()
				}

				this.httpListSubscription = this.centralControlService.listTreesPublicPanel(
					dataTablesParameters,
					this.filterText,
					this.dateStart,
					this.dateEnd,
					this.searchTruncatedStringFormated
				).subscribe(
						(resp: { content: Array<TTreeList>; numberOfPages: number }) => {
							this.trees = resp.content
							this.dataTableParams = dataTablesParameters
							callback({
								recordsFiltered: resp.numberOfPages,
								data: []
							})
						}
					)
			},
			columns: [
				{ data: 'treeId', orderable: true },
				{ data: 'specie', orderable: false },
				{ data: 'family', orderable: false },
				{ data: 'address', orderable: true },
				{ data: 'height', orderable: false },
				{ data: 'dapCap', orderable: false }
			]
		}
	}

	/**
	 * Initialize Google Map.
	 */
	public async initMap(): Promise<void> {
		this.createElementsMap('main-content', [
			{ element: 'div', attribute: 'class', valueAttribute: 'row' },
			{ element: 'div', attribute: 'class', valueAttribute: 'my-map col-12' },
			{
				element: 'div',
				attribute: 'id',
				valueAttribute: 'map-principal',
				style: 'height: 530px'
			}
		])

		const mapView = document.getElementById('map-principal')
		const map = new google.maps.Map(mapView, {
			zoom: 12,
			center: new google.maps.LatLng(this.address.lat, this.address.lng),
			gestureHandling: 'cooperative'
		})

		map.setOptions({
			styles: [
				{
					featureType: 'poi',
					stylers: [{ visibility: 'off' }]
				}
			]
		})

		if (!this.allTrees) {
			await this.getTreesForMap()
		}

		google.maps.event.addListenerOnce(map, 'idle', async () => {
			this.addMarkers(map, this.allTrees)
		})

		this.myMap = map

		if (this.allRegions.length > 0) {
			this.insertShowRegionsButtonInGooleMaps()
			this.createAllRegions()
			this.showAllRegionsInMap()
		}

		map.addListener('click', () => {
			if (this.regionInfoWindow) {
				this.regionInfoWindow.close()
			}
		})
	}

	/**
	 * Add markers to the Google Map.
	 * @param map Google Map instance
	 * @param trees Array of trees to be displayed as markers
	 */
	public addMarkers(map: google.maps.Map, trees: Array<TreeControlPanelDto>): void {
		const features: Array<TTreeFeature> = trees.map(tree =>
			this.generateFeatureForMap(tree)
		)

		this.clearMarkers()

		features.forEach(feature => this.insertFeatureInMap(map, feature))

		const options = {
			maxZoom: 20,
			styles: [
				{
					height: 53,
					url: 'https://arbolink-app.s3.amazonaws.com/2d1b-aa82-c1.png',
					width: 53
				},
				{
					height: 56,
					url: 'https://arbolink-app.s3.amazonaws.com/ee44-8e61-c2.png',
					width: 56
				},
				{
					height: 66,
					url: 'https://arbolink-app.s3.amazonaws.com/9f6f-98ec-c3.png',
					width: 66
				},
				{
					height: 78,
					url: 'https://arbolink-app.s3.amazonaws.com/0402-3f54-c4.png',
					width: 78
				},
				{
					height: 90,
					url: 'https://arbolink-app.s3.amazonaws.com/f1fa-e98f-c5.png',
					width: 90
				}
			]
		}

		const markers = this.markers.map(marker => marker.marker)
		this.markerCluster = new MarkerClusterer(this.myMap, markers, options)
		this.isLoading = false
	}

	private generateFeatureForMap(tree: TreeControlPanelDto): TTreeFeature {
		return {
			position: new google.maps.LatLng(tree.lat, tree.lng),
			icon: this.getPinIcon(tree.dapCap),
			...tree
		}
	}

	private insertFeatureInMap(
		map: google.maps.Map,
		feature: TTreeFeature
	): void {
		const marker = new google.maps.Marker({
			position: feature.position,
			icon: feature.icon,
			draggable:
				this.isAdmin ||
				this.sessionUser.assignments.includes('CENTRAL_CONTROL_PUBLIC_PANEL_DRAG_PIN_MAP')
		})

		this.markers.push({
			id: feature._id,
			marker
		})

		marker.addListener(
			'dragend',
			(event: { latLng: google.maps.LatLng | google.maps.LatLngLiteral }) => {
				const pinDragged = feature._id
				marker.setPosition(event.latLng)
				this.updateLocationPin(event.latLng as google.maps.LatLng, pinDragged)
			}
		)

		marker.addListener(
			'click',
			this.getInfosMouseClickFromTree.bind(this, map, marker, feature)
		)

		map.addListener('click', () => this.infoPopupWindow.close())
	}

	/**
	 * @param dapCap DapCap da Arvore
	 * @returns O icone a ser utilizado no mapa
	 */
	public getPinIcon(dapCap: number) {
		let scale = 0.07

		if (dapCap > 45) {
			scale = 0.1
		} else if (dapCap >= 25 && dapCap <= 45) {
			scale = 0.095
		} else if (dapCap >= 10 && dapCap < 25) {
			scale = 0.085
		} else {
			scale = 0.07
		}

		return {
			path: 'M103 145 c-72 -50 -6 -169 76 -135 77 32 54 150 -29 150 -14 0 -35 -7 -47 -15z',
			fillColor: '#008000',
			fillOpacity: 1,
			scale,
			strokeWeight: 0
		}
	}

	/**
	 * Clear existing markers from the map.
	 */
	public clearMarkers(): void {
		if (this.markers) {
			this.markers.forEach(marker => marker.marker.setMap(null))
			this.markers = []
		}

		if (this.markerCluster) {
			this.markerCluster.clearMarkers()
		}
	}

	/**
	 * Display tree information in a Google Maps infowindow.
	 * @param map Google Maps instance
	 * @param marker The selected marker
	 * @param feature The tree data associated with the marker
	 */
	public getInfosMouseClickFromTree(
		map: google.maps.Map,
		marker: google.maps.Marker,
		treeFeature: TTreeFeature
	): void {
		const treeId = treeFeature._id

		this.centralControlService.getPublicTreePanelInformation(treeId).subscribe(tree => {

				const content = this.createPopupForMap({
					identification: tree.identification,
					media: tree.mediaUrl,
					dapCap: tree.dapCap,
					height: tree.height,
					specieNomenclature: tree.specieNomenclature,
					botanicalClassification: tree.botanicalClassification
				})

				if (this.infoPopupWindow) {
					this.infoPopupWindow.close()
				}

				this.infoPopupWindow = new google.maps.InfoWindow({
					content,
					disableAutoPan: true
				})

				this.infoPopupWindow.open(map, marker)
			})
	}

	/**
	 * Create a popup content for map markers.
	 * @param data Data to be displayed in the popup
	 */
	public createPopupForMap(data: TreePopUp): string {
		return `<div style="display: flex; align-items: center;">
					<div class="initPoput" style="position: absolute; top: 0; left: 0; height: 100%; width: 20px; background-color: #FA7B00;"></div>
					<img style="width: 200px; height: 200px; margin: 0 15px 0 20px; border-radius: 20px;" class="image-tree" src="${data.media || 'assets/images/placeholder_report.svg'}"/>
					<div style="width: 280px;">
						<p style="margin-top: 10px; display: ${data.identification ? 'block' : 'none'};">
							<mark style="background-color: transparent; font-weight: bold; color: #575651;">Identificação:</mark> ${data.identification || '-'}
						</p>
						<p style="margin-top: 10px; display: ${data.dapCap ? 'block' : 'none'};">
							<mark style="background-color
							: transparent; font-weight: bold; color: #575651;">Dap/Cap (cm):</mark> ${data.dapCap || '-'}
						</p>
						<p style="margin-top: 10px; display: ${data.height ? 'block' : 'none'};">
							<mark style="background-color: transparent; font-weight: bold; color: #575651;">Cadastro:</mark> ${data.height || '-'}
						</p>
						<p style="margin-top: 10px; display: ${data.specieNomenclature ? 'block' : 'none'};">
							<mark style="background-color: transparent; font-weight: bold; color: #575651;">Espécie:</mark> ${data.specieNomenclature || '-'}
						</p>
						<p style="margin-top: 10px; display: ${data.botanicalClassification ? 'block' : 'none'};">
							<mark style="background-color: transparent; font-weight: bold; color: #575651;">Família:</mark> ${data.botanicalClassification || '-'}
						</p>
					</div>
				</div>`
	}

	/**
	 * Search trees by CEP or address.
	 */
	public async searchByCepOrAddress(): Promise<void> {
		if (!this.searchAddress || google === undefined) {
			return
		}

		const geocoder = new google.maps.Geocoder()
		geocoder.geocode(
			{ address: this.searchAddress },
			async (results: Array<{ geometry: { location: { lat: any; lng: any; }; }; }>, status: google.maps.GeocoderStatus) => {
				if (status === google.maps.GeocoderStatus.OK && results[0]) {
					const { lat, lng } = results[0].geometry.location
					this.updateMapLocation(lat, lng)
				} else if (!this.searchAddress) {
					this.resetMapLocation()
				}
			}
		)
	}

	/**
	 * Update map location based on search results.
	 * @param lat Latitude of the new location
	 * @param lng Longitude of the new location
	 */
	public async updateMapLocation(lat: number, lng: number): Promise<void> {
		const coordinates = new google.maps.LatLng(lat, lng)

		this.myMap.setCenter(coordinates)
		this.myMap.setZoom(18)
		this.setMarkerOnMap(lat, lng)
	}

	/**
	 * Reset map location to the company's address.
	 */
	public resetMapLocation(): void {
		const { lat, lng } = this.address
		const coordinates = new google.maps.LatLng(lat, lng)
		this.myMap.setCenter(coordinates)
		this.myMap.setZoom(13)

		if (this.prevMarker) {
			this.prevMarker.setMap(null)
		}
	}

	/**
	 * Set a marker on the Google Map.
	 * @param lat Latitude of the marker
	 * @param lng Longitude of the marker
	 */
	public setMarkerOnMap(lat: number, lng: number): void {
		const coordinates = new google.maps.LatLng(lat, lng)
		const iconBase = 'assets/images/m1.png'
		const icon = {
			url: iconBase,
			size: new google.maps.Size(40, 40),
			origin: new google.maps.Point(0, 0),
			anchor: new google.maps.Point(20, 40),
			labelOrigin: new google.maps.Point(21, 19)
		}

		if (this.prevMarker) {
			this.prevMarker.setMap(null)
		}

		this.prevMarker = new google.maps.Marker({
			position: coordinates,
			icon,
			animation: google.maps.Animation.DROP,
			map: this.myMap
		})

		this.myMap.addListener('click', () => this.prevMarker.setVisible(false))
	}

	/**
	 * Filter trees based on search input.
	 * @param value Search input value
	 */
	public async filterSearchTree(value: string): Promise<void> {
		const isData =
			/^(?:(?:31(\/|-|\.)(?:0?[13578]|1[02]))\1|(?:(?:29|30)(\/|-|\.)(?:0?[13-9]|1[0-2])\2))(?:(?:1[6-9]|[2-9]\d)?\d{2})$|^(?:29(\/|-|\.)0?2\3(?:(?:(?:1[6-9]|[2-9]\d)?(?:0[48]|[2468][048]|[13579][26])|(?:(?:16|[2468][048]|[3579][26])00))))$|^(?:0?[1-9]|1\d|2[0-8])(\/|-|\.)(?:(?:0?[1-9])|(?:1[0-2]))\4(?:(?:1[6-9]|[2-9]\d)?\d{2})$/

		if (isData.test(value)) {
			this.dateStart = moment_(value, 'DD/MM/YYYY').format('YYYY-MM-DD')
			this.dateEnd = moment_(value, 'DD/MM/YYYY')
				.add('days', 1)
				.format('YYYY-MM-DD')
		} else {
			this.dateStart = ''
			this.dateEnd = ''
			this.filterText =
				value.toLowerCase() === 'muito alto' ? 'muito_alto' : value
		}

		this.rerender()
	}

	public updateDataTable(search) {
		this.filterSearchTree(search)
	}

	/**
	 * Rerender DataTable.
	 */
	public rerender(): void {
		if (this.dtElement.dtInstance) {
			this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
				document.querySelector('table').classList.add('hide')
				dtInstance.destroy()
				this.refreshTable.next()
				setTimeout(
					() => document.querySelector('table').classList.remove('hide'),
					500
				)
			})
		} else {
			this.refreshTable.next()
		}
	}

	/**
	 * Update location pin for a tree.
	 * @param latlng The new latitude and longitude
	 * @param treeId The ID of the tree
	 */
	public async updateLocationPin(
		latlng: google.maps.LatLng,
		treeId: string
	): Promise<void> {
		await this.centralControlService
			.updatePinLocation(treeId, {
				lat: latlng.lat(),
				lng: latlng.lng()
			})
			.toPromise();

		(await this.dtElement.dtInstance).ajax.reload()
	}

	/**
	 * Update map cache.
	 */
	public updateMapCache() {
		this.isLoading = true

		this.centralControlService
			.updateMapCache(this.companyId)
			.subscribe(trees => {
				this.allTrees = trees
				this.addMarkers(this.myMap, trees)
				this.isLoading = false

				this.isLoadingMapCacheUpdate = true
				const loadingBar = document.querySelector('#loading-bar-map-cache') as HTMLElement

				// Iniciar o preenchimento da barra de progresso de delay
				loadingBar.style.width = '100%'

				// Parar o carregamento após 30 segundos
				setTimeout(() => {
					loadingBar.style.transition = 'none'
					loadingBar.style.width = '0%'

					setTimeout(() => {
						loadingBar.style.transition = 'width 30s linear'
					}, 1000)

					this.isLoadingMapCacheUpdate = false
				}, 30000)
			})
	}

	/**
	 * Reload map
	 */
		public reloadMap() {
			this.isLoading = true
			this.isReloadingMap = true
			this.centralControlService
				.getMapPins(this.companyId)
				.subscribe(trees => {
					this.allTrees = trees
					this.addMarkers(this.myMap, trees)
					this.isLoading = false

					this.isReloadingMap = true
					const loadingBar = document.querySelector('#loading-bar-reload-map') as HTMLElement

					// Iniciar o preenchimento da barra de progresso de delay
					loadingBar.style.width = '100%'

					// Parar o carregamento após 30 segundos
					setTimeout(() => {
						loadingBar.style.transition = 'none'
						loadingBar.style.width = '0%'

						setTimeout(() => {
							loadingBar.style.transition = 'width 10s linear'
						}, 1000)

						this.isReloadingMap = false
					}, 10000)
				})
		}

	/**
	 * Fetch regions data.
	 */
	public async fetchRegions(): Promise<void> {
		try {
			this.allRegions = await this.regionsService
				.getRegionsFromMapByCompanyId(this.companyId)
				.toPromise()
		} catch (err) {
			console.log('Error fetching regions', err)
		}
	}

	/**
	 * Insert button to toggle region visibility on Google Maps.
	 */
	public insertShowRegionsButtonInGooleMaps(): void {
		const iconBase: string = 'assets/images/icon/hide-icon.svg'
		const svgIcon = document.createElement('img')
		svgIcon.setAttribute('id', 'img-show-all-regions')
		svgIcon.setAttribute('src', iconBase)

		const button = document.createElement('button')
		button.setAttribute('class', 'btn-google-maps regions-button')
		button.appendChild(svgIcon)

		button.addEventListener('click', () => {
			this.viewAllRegionsInMap = !this.viewAllRegionsInMap
			this.switchShowHideRegionsInMap()
			this.updateImageButtonShowHideRegions()
		})

		this.myMap.controls[google.maps.ControlPosition.RIGHT_TOP].push(button)
	}

	/**
	 * Create polygons for all regions on the Google Map.
	 */
	public createAllRegions(): void {
		this.allRegions.forEach(region => {
			const polygon = new google.maps.Polygon({
				strokeOpacity: 0.6,
				strokeWeight: 2,
				fillColor: region.color,
				fillOpacity: 0.5,
				paths: region.coordinates
			})

			polygon.addListener('click', event => {
				const clickLatLng = event.latLng

				this.regionInfoWindow.setContent(region.name)
				this.regionInfoWindow.setPosition(clickLatLng)
				this.regionInfoWindow.open(this.myMap)
			})

			this.polygonsDelimitAllRegions.push(polygon)
		})
	}

	/**
	 * Toggle visibility of all regions on the map.
	 */
	public switchShowHideRegionsInMap(): void {
		if (this.viewAllRegionsInMap) {
			this.showAllRegionsInMap()
		} else {
			this.hideAllRegionsInMap()
		}
	}

	/**
	 * Show all regions on the map.
	 */
	public showAllRegionsInMap(): void {
		this.polygonsDelimitAllRegions.forEach(polygon =>
			polygon.setMap(this.myMap)
		)
	}

	/**
	 * Hide all regions from the map.
	 */
	public hideAllRegionsInMap(): void {
		this.polygonsDelimitAllRegions.forEach(polygon => polygon.setMap(null))
		this.regionInfoWindow.close()
	}

	/**
	 * Update the image of the button that toggles region visibility.
	 */
	public updateImageButtonShowHideRegions(): void {
		const iconBase = `assets/images/icon/${this.viewAllRegionsInMap ? 'hide-icon' : 'show-icon'}.svg`
		const imageButton: HTMLImageElement = document.getElementById(
			'img-show-all-regions'
		) as HTMLImageElement
		if (imageButton) {
			imageButton.setAttribute('src', iconBase)
		}
	}

	/**
	 * Handle search truncated value change.
	 * @param value The new search truncated value
	 */
	public searchTruncated(value: string): void {
		this.searchTruncatedStringFormated = value
		this.rerender()
	}

	/**
	 * Update the length of the table.
	 * @param newLength The new length of the table
	 */
	public updateLengthTable(newLength: string): void {
		this.datatableOptions.pageLength = parseInt(newLength)
		this.rerender()
		setLengthTable(newLength)
	}

	/**
	 * Scroll to the top of the table.
	 */
	public runToInitTable(): void {
		const table: HTMLDivElement = document.getElementsByClassName(
			'datatables-content'
		)[0] as HTMLDivElement
		scrollToConteiner(table)
		insertOrRemoveClassInScrollButton()
	}

	public createElementsMap(classMain: string, elements: Array<ElementConfig>): void {
		// Obter o container principal onde os elementos serão inseridos
		const containerMain = document.getElementsByClassName(classMain)[0]
		if (!containerMain) {
			console.error(`Container com a classe ${classMain} não foi encontrado.`)
			return
		}

		// Criar os elementos com base na configuração fornecida
		const elementsCreated = this.createElements(elements)

		// Inserir o primeiro elemento criado no container principal
		const containerMap = elementsCreated[0]
		containerMain.insertBefore(containerMap, containerMain.children[1])

		// Organizar os elementos dentro do container principal e seus filhos
		this.organizeElements(containerMap, elementsCreated)
	}

	/**
	 * Organiza os elementos criados dentro do container principal e seus filhos.
	 * @param containerMap O elemento container principal.
	 * @param elementsCreated Array de elementos criados.
	 */
	public organizeElements(
		containerMap: HTMLElement,
		elementsCreated: Array<HTMLElement>
	): void {
		// Iterar sobre os elementos criados e adicioná-los ao container apropriado
		elementsCreated.forEach((element, index) => {
			if (index > 0 && index <= 1) {
				containerMap.appendChild(element)
			} else if (index > 1 && index <= 4) {
				elementsCreated[1].appendChild(element)
			} else if (index > 4) {
				elementsCreated[4].appendChild(element)
			}
		})
	}

	/**
	 * Cria elementos DOM com base na configuração fornecida.
	 * @param elements Array de configurações de elementos.
	 * @returns Array de elementos criados.
	 */
	public createElements(elements: Array<ElementConfig>): Array<HTMLElement> {
		return elements.map(elementConfig => {
			const element = document.createElement(elementConfig.element)
			element.setAttribute(
				elementConfig.attribute,
				elementConfig.valueAttribute
			)

			if (elementConfig.style) {
				element.setAttribute('style', elementConfig.style)
			}
			if (elementConfig.text) {
				element.innerHTML = elementConfig.text
			}
			if (elementConfig.action) {
				element.addEventListener('click', elementConfig.action)
			}

			return element
		})
	}

	public updateCacheMapPublicPanel() {
		if (
			!(this.isAdmin ||
				this.sessionUser.assignments.includes(
					'CENTRAL_CONTROL_PUBLIC_PANEL_UPDATE_MAP_CACHE'
				))
		) {
			console.log(
				'Usuário não tem permissão para atualizar o cache do mapa público'
			)
			return
		}

		this.isLoading = true

		this.centralControlService.updateMapCacheInPublicPanel().subscribe(
			() => {
				console.log('Cache do mapa público atualizado com sucesso')
				this.notificationService.notify(
					'Cache do mapa público atualizado com sucesso',
					200
				)

			 this.centralControlService
				.getMapPins(this.companyId)
				.subscribe(trees => {
					this.allTrees = trees
					this.addMarkers(this.myMap, trees)
					this.isLoading = false
				}, err => {
					console.error('Erro ao atualizar o cache do mapa público', err)
					this.notificationService.notify(
						'Erro ao recarregar o mapa, mas a publicação foi bem sucedida, atualize a página!',
						400
					)
				})
			},
			error => {
				console.error('Erro ao atualizar o cache do mapa público', error)
				this.notificationService.notify(
					'Erro ao atualizar o cache do mapa público',
					400
				)
			}
		)

		const loadingBar = document.querySelector('#loading-bar-public-map-cache') as HTMLElement

		// Desabilite o botão de atualização dos dados do mapa público por 30 segundos
		this.isLoadingPublicMapCacheUpdate = true

		// Iniciar o preenchimento da barra de progresso de delay

		loadingBar.style.width = '100%'

		// Parar o carregamento após 30 segundos
		setTimeout(() => {
			this.isLoadingPublicMapCacheUpdate = false
			loadingBar.style.transition = 'none'
			loadingBar.style.width = '0%'

			setTimeout(() => {
				loadingBar.style.transition = 'width 30s linear'
			}, 1000)
		}, 30000)
	}

	public getInfosMouseClickOnTable(tree: TTreeList, index: number): void {
		if (!this.trees[index].enabledForPublicPanel) {
			console.log('Árvore não publicada')
			return
		}

		this.centralControlService.getPublicTreePanelInformation(tree._id).subscribe(tree => {
				if (tree && tree.lat && tree.lng) {
					const latLng = new google.maps.LatLng(
						tree.lat,
						tree.lng
					)

					this.myMap.setCenter(latLng)
				}

				this.myMap.setZoom(21)

				const content = this.createPopupForMap({
					identification: tree.identification,
					media: tree.mediaUrl,
					dapCap: tree.dapCap,
					height: tree.height,
					specieNomenclature: tree.specieNomenclature,
					botanicalClassification: tree.botanicalClassification
				})

				if (this.infoPopupWindow) {
					this.infoPopupWindow.close()
				}

				this.infoPopupWindow = new google.maps.InfoWindow({
					content,
					disableAutoPan: true
				})

				const marker = this.markers.find(marker => marker.id === tree._id).marker
				console.log('Marker', marker)

				this.infoPopupWindow.open(this.myMap, marker)

				const mapBody = document.getElementById('map-principal')

				if (mapBody) {
					mapBody.scrollIntoView({
						behavior: 'smooth'
					})
				}
			}, error => {
				console.error('Erro ao buscar informações da árvore', error)
				this.notificationService.notify('Erro ao buscar informações da árvore', 400)
			})
	}

	public toggleUnpublishAllPublicTreeInList(): void {
		if (
			!(this.isAdmin ||
				this.sessionUser.assignments.includes(
					'CENTRAL_CONTROL_PUBLIC_PANEL_PUBLISH_TREE'
				))
		) {
			console.log(
				'Usuário não tem permissão para publicar árvores'
			)
			return
		}

		const treesToDisable = this.trees
			.filter(tree => tree.enabledForPublicPanel)
			.map(tree => tree._id)

		if (treesToDisable.length > 0) {
			this.centralControlService
				.updateTreePublicPanel(treesToDisable, 'disable')
				.subscribe(() => {
					this.trees = this.trees.map(tree => {
						if (treesToDisable.includes(tree._id)) {
							tree.enabledForPublicPanel = !tree.enabledForPublicPanel
						}

						return tree
					})

					this.removeMarkersFromPublishedTrees(treesToDisable)
				}, err => {
					console.error('Erro ao despublicar todas as árvores', err)
					this.notificationService.notify('Erro ao despublicar todas as árvores', 400)
				})
		}
	}

	public togglePublishAllPublicTreeInList(): void {
		if (
			!(this.isAdmin ||
				this.sessionUser.assignments.includes(
					'CENTRAL_CONTROL_PUBLIC_PANEL_PUBLISH_TREE'
				))
		) {
			console.log(
				'Usuário não tem permissão para publicar árvores'
			)
			return
		}

		const treesToEnable = this.trees
			.filter(tree => !tree.enabledForPublicPanel)

		const treesFilteredToEnable = treesToEnable.map(tree => tree._id)
		if (treesToEnable.length > 0) {
			this.centralControlService
				.updateTreePublicPanel(treesFilteredToEnable, 'enable')
				.subscribe(() => {
					this.trees = this.trees.map(tree => {
						if (treesFilteredToEnable.includes(tree._id)) {
							tree.enabledForPublicPanel = !tree.enabledForPublicPanel
						}

						return tree
					})

					this.createMarkersOnMapForNewPublishedTrees(treesToEnable)
				}, err => {
					console.error('Erro ao publicar todas as árvores', err)
					this.notificationService.notify('Erro ao publicar todas as árvores', 400)
				})
		}
	}

	public togglePublicTree(tree: TTreeList, index: number): void {
		if (
			!(this.isAdmin ||
				this.sessionUser.assignments.includes(
					'CENTRAL_CONTROL_PUBLIC_PANEL_PUBLISH_TREE'
				))
		) {
			console.log(
				'Usuário não tem permissão para publicar árvores'
			)
			return
		}

		const action = !tree.enabledForPublicPanel ? 'enable' : 'disable'

		this.centralControlService.updateTreePublicPanel([tree._id], action).subscribe(() => {
			this.trees[index].enabledForPublicPanel = !tree.enabledForPublicPanel

			if (action === 'enable') {
				this.createMarkersOnMapForNewPublishedTrees([tree])
			} else {
				this.removeMarkersFromPublishedTrees([tree._id])
			}
		})
	}

	public removeMarkersFromPublishedTrees(treeIds: Array<string>): void {
		const treesFilteredToRemove = this.allTrees.filter(tree => treeIds.includes(tree._id))

		if (treesFilteredToRemove.length > 0) {
			this.markerCluster.removeMarkers(this.markers.filter(marker => treeIds.includes(marker.id)).map(marker => marker.marker), false)
		}
	}

	public createMarkersOnMapForNewPublishedTrees(trees: Array<TTreeList>): void {
		if (trees.length > 0) {
			const markers = trees.map(tree => {
				const feature: TTreeFeature = {
					_id: tree._id,
					botanicalClassification: tree.botanicalClassification,
					dapCap: tree.dapCap,
					height: tree.height,
					lat: tree.address.location[0],
					lng: tree.address.location[1],
					specieNomenclature: tree.specieNomenclature,
					icon: this.getPinIcon(tree.dapCap),
					position: new google.maps.LatLng(tree.address.location[0], tree.address.location[1])
				}

				const marker = new google.maps.Marker({
					position: feature.position,
					icon: feature.icon,
					draggable: false
				})

				marker.addListener('click', () => {
					this.getInfosMouseClickFromTree(this.myMap, marker, feature)
				})

				const markerGenerated = {
					id: feature._id,
					marker
				}

				this.markers.push(markerGenerated)

				return markerGenerated
			})

			this.markerCluster.addMarkers(markers.map(marker => marker.marker), false)
		}
	}
}
